/**
 * React Query hooks for library aliases
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getLibraryAliases,
  createLibraryAlias,
  updateLibraryAlias,
  deleteLibraryAlias,
} from '@/api/libraries';
import { LibraryAlias, CreateLibraryAliasRequest, UpdateLibraryAliasRequest } from '@/types/api';
import { getErrorMessage } from '@/api/client';

/**
 * Hook to fetch library aliases
 */
export const useLibraryAliases = (libraryId: number) => {
  return useQuery<LibraryAlias[], Error>({
    queryKey: ['libraries', libraryId, 'aliases'],
    queryFn: () => getLibraryAliases(libraryId),
    enabled: !!libraryId,
  });
};

/**
 * Hook to create library alias
 */
export const useCreateLibraryAlias = () => {
  const queryClient = useQueryClient();

  return useMutation<
    LibraryAlias,
    Error,
    { libraryId: number; data: CreateLibraryAliasRequest }
  >({
    mutationFn: ({ libraryId, data }) => createLibraryAlias(libraryId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['libraries', variables.libraryId, 'aliases'],
      });
    },
    onError: (error) => {
      console.error('Failed to create alias:', getErrorMessage(error));
    },
  });
};

/**
 * Hook to update library alias
 */
export const useUpdateLibraryAlias = () => {
  const queryClient = useQueryClient();

  return useMutation<
    LibraryAlias,
    Error,
    { libraryId: number; aliasId: number; data: UpdateLibraryAliasRequest }
  >({
    mutationFn: ({ libraryId, aliasId, data }) =>
      updateLibraryAlias(libraryId, aliasId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['libraries', variables.libraryId, 'aliases'],
      });
    },
    onError: (error) => {
      console.error('Failed to update alias:', getErrorMessage(error));
    },
  });
};

/**
 * Hook to delete library alias
 */
export const useDeleteLibraryAlias = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { libraryId: number; aliasId: number }>({
    mutationFn: ({ libraryId, aliasId }) => deleteLibraryAlias(libraryId, aliasId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['libraries', variables.libraryId, 'aliases'],
      });
    },
    onError: (error) => {
      console.error('Failed to delete alias:', getErrorMessage(error));
    },
  });
};
